/**
 * Pipefitter API client.
 * Uses REACT_APP_PIPEFITTER_BASE_URL from environment.
 * In development, requests go through the dev server proxy (/api/pipefitter) to avoid CORS
 * (browser blocks cross-origin responses from API Gateway unless CORS is configured there).
 * Same auth (Data 360 JWT) and error handling as main app.
 */
import axios from 'axios';
import get from 'lodash/get';
import { getJwtToken } from './auth';

// In dev, use same-origin proxy so browser doesn't hit CORS; proxy forwards to real API
const baseURL =
  process.env.NODE_ENV === 'development'
    ? '/api/pipefitter/v1'
    : process.env.REACT_APP_PIPEFITTER_BASE_URL;

const pipefitterApi = axios.create({
  baseURL: process.env.REACT_APP_PIPEFITTER_BASE_URL,
// baseURL: 'http://localhost:9000/v1',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

/** Long-running endpoints that may 504 at the gateway while work continues server-side. */
const ASYNC_GATEWAY_PATHS = [
  '/sequence/pipe-based-generate',
  '/sequence/calculate-min-max-sequence/',
  '/reportgen/complimentary-report/',
  '/reportgen/pipe-data/',
];

function requestUrl(config) {
  if (!config) return '';
  return `${config.baseURL || ''}${config.url || ''}`;
}

function isAsyncGatewayRequest(config) {
  const url = requestUrl(config);
  return ASYNC_GATEWAY_PATHS.some((path) => url.includes(path));
}

function isComplimentaryReportRequest(config) {
  return requestUrl(config).includes('/reportgen/complimentary-report/');
}

function isPipeDataReportRequest(config) {
  return requestUrl(config).includes('/reportgen/pipe-data/');
}

function isGatewayTimeoutStatus(status) {
  const code = Number(status);
  return code === 504 || code === 502 || code === 503;
}

function isAsyncGatewayTimeoutError(error) {
  const statusCode = Number(get(error, ['response', 'status'], NaN));
  const errData = get(error, ['response', 'data'], undefined);
  const bodyStatus = errData && typeof errData === 'object' ? Number(errData.status) : NaN;

  if (isGatewayTimeoutStatus(statusCode) || isGatewayTimeoutStatus(bodyStatus)) {
    return true;
  }
  if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK') return true;

  const message = String(error?.message || '').toLowerCase();
  return (
    message === 'network error' ||
    message.includes('504') ||
    message.includes('502') ||
    message.includes('503') ||
    message.includes('gateway timeout') ||
    message.includes('timeout') ||
    message.includes('timed out')
  );
}

function isMinMaxCalculationRequest(config) {
  return requestUrl(config).includes('/sequence/calculate-min-max-sequence/');
}

function syntheticAsyncGatewayResponse(config) {
  if (isComplimentaryReportRequest(config)) {
    return {
      status: 200,
      statusText: 'OK',
      data: {
        success: 1,
        status: 200,
        message: 'The pipe compatibility report is being generated.',
        data: {},
      },
      headers: {},
      config,
    };
  }

  if (isPipeDataReportRequest(config)) {
    return {
      status: 200,
      statusText: 'OK',
      data: {
        success: 1,
        status: 200,
        message: 'The pipe data report is being generated.',
        data: {},
      },
      headers: {},
      config,
    };
  }

  if (isMinMaxCalculationRequest(config)) {
    return {
      status: 200,
      statusText: 'OK',
      data: {
        success: 1,
        status: 200,
        message: 'Calculation started',
        data: {},
      },
      headers: {},
      config,
    };
  }

  return {
    status: 200,
    statusText: 'OK',
    data: {
      success: 1,
      status: 200,
      message: 'Sequence generation started',
      data: {},
    },
    headers: {},
    config,
  };
}

const handleErroredResponse = async (error) => {
  const originalRequest = get(error, ['response', 'config'], error?.config);
  const statusCode = get(error, ['response', 'status'], undefined);
  const errMessage = get(error, ['response', 'data', 'name'], undefined);
  const errData = get(error, ['response', 'data'], undefined);

  if (isAsyncGatewayRequest(originalRequest) && isAsyncGatewayTimeoutError(error)) {
    return syntheticAsyncGatewayResponse(originalRequest);
  }

  if (statusCode === 401) {
    if (errMessage === 'TokenExpiredError') {
      const token = await getJwtToken();
      if (token) {
        pipefitterApi.defaults.headers.common['Authorization'] = token;
      }
      return pipefitterApi(originalRequest);
    }
  }

  if (statusCode === 410) {
    return Promise.reject(error);
  }

  if (errData && typeof errData === 'object') {
    return Promise.reject(errData);
  }
  if (errData && typeof errData === 'string') {
    return Promise.reject(new Error(errData));
  }
  if (error.message) {
    return Promise.reject(new Error(error.message));
  }
  return Promise.reject(new Error('An unknown error occurred'));
};

pipefitterApi.interceptors.request.use(
  async (config) => {
    const authToken = await getJwtToken();
    if (authToken) {
      config.headers.Authorization = authToken;
    }
    // Don't set Content-Type for FormData - let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

pipefitterApi.interceptors.response.use(
  (response) => {
    if (isAsyncGatewayRequest(response.config) && isGatewayTimeoutStatus(response.status)) {
      return syntheticAsyncGatewayResponse(response.config);
    }
    return response;
  },
  async (error) => handleErroredResponse(error)
);

export default pipefitterApi;
